








import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { mapGetters } from 'vuex';

import { MobileGetter } from 'frontend/types';
import { RelatedLearningPathInfo } from 'frontend/types/learningPathV2';

@Component({
  computed: {
    ...mapGetters('pageHeader', ['isMobile']),
    ...mapGetters('relatedLearningPathInfo', ['learningPath']),
  },
})
export default class OnboardingOverlay extends Vue {
  @Prop({ type: String, required: true })
  private firstChapterName!: string;

  private readonly isMobile!: MobileGetter;
  private readonly learningPath?: RelatedLearningPathInfo;

  private modalStyles: { [key: string]: string } = {};
  private clonedRecommendedLesson: HTMLElement | null = null;
  private recommendedLesson: HTMLElement | null = null;
  private clonedCover: HTMLElement | null = null;
  private cover: HTMLElement | null = null;

  private get isInLLPRolloutExperiment(): boolean {
    return !!this.learningPath;
  }

  mounted(): void {
    this.highlightFirstLesson();
    if (this.isInLLPRolloutExperiment) {
      // $nextTick is needed to wait for DOM changes in the cover before cloning it.
      this.$nextTick(this.highlightCover);
    }
    document.body.classList.add('no-scroll');
  }

  destroyed() {
    document.body.classList.remove('no-scroll');
    this.closeOverlay();
  }

  private highlightFirstLesson(): void {
    const positionModalBelowElement = (element: HTMLElement): void => {
      const elementClientRect = element.getBoundingClientRect();
      const horizontalOffset = 24;
      const verticalOffset = 20;

      if (this.isMobile(this.$mq)) {
        this.modalStyles = {
          left: '12px',
          top: `${elementClientRect.y + elementClientRect.height + verticalOffset}px`,
        };
      } else {
        const modalWidth = 480;

        this.modalStyles = {
          left: `${
            elementClientRect.x + (elementClientRect.width - modalWidth) / 2 - horizontalOffset
          }px`,
          top: `${elementClientRect.y + elementClientRect.height + verticalOffset}px`,
        };
      }
    };
    const removeUnneededElements = (element: HTMLElement, selectors: string[]): void => {
      selectors.forEach(selector => {
        const elementToRemove = element.querySelector<HTMLElement>(selector);
        if (elementToRemove) {
          elementToRemove.style.display = 'none';
        }
      });
    };

    this.recommendedLesson = document.querySelector<HTMLElement>('.lesson.is-recommended');

    if (this.recommendedLesson) {
      this.clonedRecommendedLesson = this.cloneElementAndPutItInFront(this.recommendedLesson);
      removeUnneededElements(this.clonedRecommendedLesson, ['.vertical-line', '.practice-lessons']);

      if (this.isInLLPRolloutExperiment) {
        const shadowBox = this.createShadowBox();
        if (this.isMobile(this.$mq)) {
          shadowBox.style.top = '-16px';
          shadowBox.style.left = '-16px';
          shadowBox.style.width = 'calc(100% + 32px)';
        } else {
          shadowBox.style.top = '-16px';
          shadowBox.style.left = '36px';
          shadowBox.style.width = 'calc(100% - 72px)';
        }
        shadowBox.style.height = '114px';

        this.clonedRecommendedLesson.appendChild(shadowBox);
      }

      document.body.appendChild(this.clonedRecommendedLesson);
      positionModalBelowElement(this.clonedRecommendedLesson);

      // The original element stays but it should not be visible, in that way the layout is not altered.
      this.recommendedLesson.style.visibility = 'hidden';
    } else {
      this.closeOverlay();
    }
  }

  private highlightCover(): void {
    this.cover = document.querySelector<HTMLElement>('.cover-wrapper');
    if (this.cover) {
      this.clonedCover = this.cloneElementAndPutItInFront(this.cover);
      const shadowBox = this.createShadowBox();
      this.clonedCover.appendChild(shadowBox);

      document.body.appendChild(this.clonedCover);

      // The original element stays but it should not be visible, in that way the layout is not altered.
      this.cover.style.visibility = 'hidden';
    } else {
      this.closeOverlay();
    }
  }

  private cloneElementAndPutItInFront(originalElement: HTMLElement): HTMLElement {
    const clonedElement = originalElement.cloneNode(true) as HTMLElement;
    const originalElementClientRect = originalElement.getBoundingClientRect();
    const clonedElementAndModalBottom = originalElementClientRect.bottom + 160;
    const isEnoughSpaceForModal = clonedElementAndModalBottom < window.innerHeight;
    let offset = 0;

    if (!this.isInLLPRolloutExperiment && !isEnoughSpaceForModal) {
      offset = Math.abs(window.innerHeight - clonedElementAndModalBottom);
      window.scroll({ top: offset, behavior: 'smooth' });
    }

    clonedElement.style.position = 'fixed';
    clonedElement.style.top = `${originalElementClientRect.top - offset}px`;
    clonedElement.style.left = `${originalElementClientRect.x}px`;
    clonedElement.style.zIndex = '700';
    clonedElement.style.backgroundColor = '#fff';
    clonedElement.style.width = `${originalElementClientRect.width}px`;
    clonedElement.style.height = `${originalElementClientRect.height}px`;

    return clonedElement;
  }

  private createShadowBox(): HTMLElement {
    const shadowBox = document.createElement('div');
    shadowBox.style.position = 'absolute';
    shadowBox.style.top = '0';
    shadowBox.style.height = '100%';
    shadowBox.style.width = '100%';
    shadowBox.style.zIndex = '-1';
    shadowBox.style.boxShadow = '0px 0px 15px rgba(0, 0, 0, 0.1)';
    shadowBox.style.borderRadius = '10px';

    return shadowBox;
  }

  private closeOverlay(): void {
    if (this.recommendedLesson) {
      this.recommendedLesson.style.visibility = 'unset';
      this.recommendedLesson = null;
    }
    if (this.clonedRecommendedLesson) {
      this.clonedRecommendedLesson.remove();
      this.clonedRecommendedLesson = null;
    }
    if (this.cover) {
      this.cover.style.visibility = 'unset';
      this.cover = null;
    }
    if (this.clonedCover) {
      this.clonedCover.remove();
      this.clonedCover = null;
    }
    this.$emit('overlay-closed');
  }
}
